import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import moment from "moment";

import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import { StyledButton } from "@/components";
import useFetchCommonData from "@/hooks/useFetchCommonData";
import {
  useAddAttendanceLeaveApprovalMutation,
  useGetLeaveApplicationQuery,
} from "@/api/services/attendanceManagement/monthEndProcess/MonthEndProcess";
import { useFetchLeaveTypeCategoryQuery } from "@/api/services/leaveManagement/LeaveTypeCategory";
import { toast } from "@/components/toastify";
import CommonDataTable from "@/components/dataTableComponents/NewDatatablePagination";

const LeaveApplication = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowActions, setRowActions] = useState({});
  const [rowRemarks, setRowRemarks] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [changeValue, setChangeValue] = useState({
    selectEmployee: "",
    selectLeaveType: "",
    selectMonth: "",
    sortDirection: "DESC",
    page: 0,
    pageSize: 10,
    search: ""
  });
  const [loading, setLoading] = useState(false);

  const { data: fetchAllEmployees } = useFetchCommonData("ALLEMPLOYEES");
  const { data: fetchLeaveTypeCategory = [] } = useFetchLeaveTypeCategoryQuery();
  const [updateLeave] = useAddAttendanceLeaveApprovalMutation();

  // ✅ Skip initial automatic fetch, call manually
  const {
    data: rowDetails = [],
    refetch,
    isLoading
  } = useGetLeaveApplicationQuery(
    {
      leaveType: changeValue.selectLeaveType,
      empId: changeValue.selectEmployee,
      month: changeValue.selectMonth,
      sortDirection: "DESC",
      page: changeValue.page,
      size: changeValue.pageSize,
      search: changeValue.search
    }
  );

  // Fetch data only when filters/search/page changes (not on mount)
  useEffect(() => {
    // Only refetch if any filter/search/page changes (skip initial automatic fetch)
    if (
      changeValue.selectLeaveType ||
      changeValue.selectEmployee ||
      changeValue.selectMonth ||
      changeValue.page > 0 ||
      changeValue.pageSize !== 10 ||
      changeValue.search
    ) {
      refetch();
    }
  }, [
    changeValue.selectLeaveType,
    changeValue.selectEmployee,
    changeValue.selectMonth,
    changeValue.page,
    changeValue.pageSize,
    changeValue.search,
    refetch
  ]);


  // Fetch data manually on mount and when filters/page/search change
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData, changeValue]);

  // pagination handlers
  const handleSearchChange = useCallback((value) => {
    setChangeValue(prev => ({ ...prev, search: value, page: 0 }));
    setCurrentPage(0);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    setChangeValue(prev => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    setChangeValue(prev => ({ ...prev, pageSize: newPageSize, page: 0 }));
  }, []);

  // Map API data to table rows
  const newrows = (rowDetails?.data?.content || []).map(item => ({
    id: item.id,
    PendingWith: item.appliedToName,
    EmployeeNo: item.empId,
    EmployeeName: item.empName,
    Category: item.leaveCategory,
    leaveType: item.leaveType,
    duration: item?.dateList?.join(" - ") || "NA",
    days: item.numOfDays,
    reason: item.empReason,
    appliedOn: item.appliedDate || "NA",
    action: item.status,
    remarks: item.remarks,
    leaveCode: item.leaveCode,
  }));

  const empOptions = fetchAllEmployees?.map(item => ({
    value: item.empId,
    label: item.employeeName,
  })) || [];

  const leaveOptions = fetchLeaveTypeCategory?.data?.map(item => ({
    value: item.leaveTypeName,
    label: item.leaveTypeName,
  })) || [];

  const removeValidationError = (id, field) => {
    setValidationErrors(prev => {
      const updated = { ...prev[id] };
      delete updated[field];
      const newErrors = { ...prev };
      if (Object.keys(updated).length > 0) newErrors[id] = updated;
      else delete newErrors[id];
      return newErrors;
    });
  };

  const validateSelectedRows = (selectedData) => {
    const errors = {};
    selectedData.forEach(({ id }) => {
      const action = rowActions[id];
      const remark = rowRemarks[id];

      // Only require remark if Reject
      if (action === "reject" && !remark) {
        errors[id] = { remark: "Remark is required when rejecting" };
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSelectedRowsChange = (newSelectedRows) => {
    const selectedIds = newSelectedRows.map(row => row.id);
    const newErrors = {};
    const newRowActions = {};
    const newRowRemarks = {};

    newSelectedRows.forEach(row => {
      const id = row.id;
      const action = rowActions[id];
      const remark = rowRemarks[id];

      if (action === "reject" && !remark) {
        newErrors[id] = { remark: "Remark is required when rejecting" };
      }

      if (action) newRowActions[id] = action;
      if (remark) newRowRemarks[id] = remark;
    });

    setValidationErrors(prev => {
      const updated = {};
      for (const id of selectedIds) {
        if (newErrors[id]) updated[id] = newErrors[id];
        else if (prev[id]) updated[id] = prev[id];
      }
      return updated;
    });

    setRowActions(prev => {
      const updated = {};
      for (const id of selectedIds) {
        updated[id] = newRowActions[id] || prev[id] || "approve";
      }
      return updated;
    });

    setRowRemarks(prev => {
      const updated = {};
      for (const id of selectedIds) {
        updated[id] = newRowRemarks[id] || prev[id] || "";
      }
      return updated;
    });

    setSelectedRows(newSelectedRows);
  };

  // const handleReset = (resetForm) => {
  //   resetForm({ values: { selectLeaveType: "", selectEmployee: "", selectMonth: "" } });
  //   // setChangeValue({
  //   //   selectLeaveType: "",
  //   //   selectEmployee: "",
  //   //   selectMonth: "",
  //   //   page: 0,
  //   //   pageSize,
  //   //   search: ""
  //   // });
  //    setChangeValue((prev) => ({
  //             ...prev,
  //             selectLeaveType: "",
  //             selectEmployee: "",
  //             selectMonth: "",
  //             page: 0,
  //             pageSize,
  //             search: "",
  //           }));
  //   setCurrentPage(0);
  //   setRowActions({});
  //   setRowRemarks({});
  //   setSelectedRows([]);
  //   setValidationErrors({});
  //   fetchData();
  // };

  const handleSubmit = async () => {
    if (!validateSelectedRows(selectedRows)) return;

    try {
      const payload = selectedRows.map(({ id }) => {
        const row = newrows.find(r => r.id === id);
        return {
          empId: row.EmployeeNo,
          leaveCode: row.leaveCode,
          type: rowActions[id] || "approve",
          remark: rowActions[id] === "reject" ? (rowRemarks[id] || "") : "",
        };
      });

      await updateLeave({ formData: payload }).unwrap();
      toast("success", "Leave Updated Successfully!");
      setRowActions({});
      setRowRemarks({});
      setSelectedRows([]);
      setValidationErrors({});
      fetchData();
    } catch (error) { }
  };

  const columns = [
    { field: "PendingWith", headerName: "Pending With" },
    { field: "EmployeeNo", headerName: "Employee No" },
    { field: "EmployeeName", headerName: "Employee Name" },
    { field: "Category", headerName: "Category" },
    { field: "leaveType", headerName: "Leave Type" },
    { field: "duration", headerName: "Duration" },
    { field: "days", headerName: "Days" },
    { field: "reason", headerName: "Reasons" },
    { field: "appliedOn", headerName: "Applied On" },
    // {
    //   field: "Action",
    //   headerName: "Action",
    //   flex: 4,
    //   minWidth: 200,
    //   renderCell: ({ row }) => {
    //     const error = validationErrors[row.id]?.action;
    //     return (
    //       <FormControl fullWidth sx={{ padding: "8px 0px" }}>
    //         <Select
    //           value={rowActions[row.id] || "approve"}
    //           onChange={(e) => {
    //             const value = e.target.value;
    //             setRowActions(prev => ({ ...prev, [row.id]: value }));
    //             if (error && value) removeValidationError(row.id, "action");
    //           }}
    //         >
    //           <MenuItem value="approve">Approve</MenuItem>
    //           <MenuItem value="reject">Reject</MenuItem>
    //         </Select>
    //       </FormControl>
    //     );
    //   },
    // },
    {
      field: "Action",
      headerName: "Action",
      flex: 4,
      minWidth: 200,
      renderCell: ({ row }) => {
        const error = validationErrors[row.id]?.action;
        return (
          <FormControl fullWidth sx={{ padding: "8px 0px" }}>
            <Select
              value={rowActions[row.id] || "approve"}
              onChange={(e) => {
                const value = e.target.value;
                setRowActions((prev) => ({ ...prev, [row.id]: value }));

                // ✅ If Reject → check remark immediately
                if (value === "reject" && !(rowRemarks[row.id] || "").trim()) {
                  setValidationErrors((prev) => ({
                    ...prev,
                    [row.id]: {
                      ...(prev[row.id] || {}),
                      remark: "Remark is required when rejecting"
                    }
                  }));
                } else {
                  // clear error if switching away from reject
                  removeValidationError(row.id, "remark");
                }

                if (error && value) removeValidationError(row.id, "action");
              }}
            >
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="reject">Reject</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "Remarks",
      headerName: "Remarks",
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => {
        const error = validationErrors[row.id]?.remark;
        return (
          <FormControl fullWidth sx={{ padding: "8px 0px" }}>
            <TextField
              value={rowRemarks[row.id] || ""}
              onChange={(e) => {
                const value = e.target.value;
                setRowRemarks((prev) => ({ ...prev, [row.id]: value }));

                if (error && value) removeValidationError(row.id, "remark");
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
            {error && (
              <span style={{ color: "red", fontSize: "12px" }}>{error}</span>
            )}
          </FormControl>
        );
      },
    },
    // {
    //   field: "Remarks",
    //   headerName: "Remarks",
    //   flex: 1,
    //   minWidth: 200,
    //   renderCell: ({ row }) => {
    //     const error = validationErrors[row.id]?.remark;
    //     return (
    //       <FormControl fullWidth sx={{ padding: "8px 0px" }}>
    //         <TextField
    //           value={rowRemarks[row.id] || ""}
    //           onChange={(e) => {
    //             const value = e.target.value;
    //             setRowRemarks(prev => ({ ...prev, [row.id]: value }));
    //             if (error && value) removeValidationError(row.id, "remark");
    //           }}
    //           onKeyDown={(e) => e.stopPropagation()}
    //         />
    //         {error && <span style={{ color: "red", fontSize: "12px" }}>{error}</span>}
    //       </FormControl>
    //     );
    //   },
    // },
  ];

  return (
    <Box>
      <Formik
        initialValues={{ selectEmployee: "", selectLeaveType: "", selectMonth: "" }}
        onSubmit={() => { }}
      >
        {({ resetForm, values }) => {
          useEffect(() => {
            const formattedMonth = values.selectMonth
              ? moment(values.selectMonth).format("MM-YYYY")
              : "";
            setChangeValue(prev => ({
              ...prev,
              selectEmployee: values.selectEmployee,
              selectLeaveType: values.selectLeaveType,
              selectMonth: formattedMonth,
              page: 0
            }));
          }, [values.selectMonth, values.selectEmployee, values.selectLeaveType]);

          const handleReset = (resetForm) => {
            resetForm({ values: { selectLeaveType: "", selectEmployee: "", selectMonth: "" } });
            setChangeValue((prev) => ({
              ...prev,
              selectLeaveType: "",
              selectEmployee: "",
              selectMonth: "",
              page: 0,
              pageSize,
              search: "",
            }));
            setCurrentPage(0);
            setRowActions({});
            setRowRemarks({});
            setSelectedRows([]);
            setValidationErrors({});
            fetchData();
          };


          return (
            <Form>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={3}>
                  <FormikMonthYearPicker
                    name="selectMonth"
                    label="Select Month"
                    required
                    pickerType="month"
                    placeholder="Select To Month"
                    maxDate={new Date()}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormikSelectInput
                    name="selectLeaveType"
                    label="Leave Type"
                    required
                    placeholder="Select Leave Type"
                    options={leaveOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormikSelectInput
                    name="selectEmployee"
                    label="Select Employee"
                    required
                    placeholder="Select Employee"
                    options={empOptions}
                    search
                  />
                </Grid>
                <Grid item xs={12} sm={3} mt={3.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleReset(resetForm)}
                    sx={{ width: 100, height: 40, mr: 2 }}
                  >
                    Reset
                  </Button>
                  {selectedRows.length > 0 && (
                    <StyledButton
                      variant="contained"
                      type="button"
                      onClick={handleSubmit}
                      label={selectedRows.length > 0 ? "Submit" : "Submit"}
                      sx={{ width: 100, height: 40 }}
                    />
                  )}
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>

      <CommonDataTable
        columns={columns}
        rows={newrows}
        checkbox
        disableRowSelectionOnClick
        selectedRows={selectedRows}
        onSelectedRowsChange={handleSelectedRowsChange}
        search={false}
        loading={loading || isLoading}
        onSearchChange={handleSearchChange}
        pageCount={rowDetails?.data?.totalPages || 0}
        totalRecords={rowDetails?.data?.totalElements || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        currentPageSize={pageSize}
      />
    </Box>
  );
};

export default LeaveApplication;
